import type { StatusIndicatorProps } from '@cloudscape-design/components/status-indicator';
import type { VpnPeerStatus, VpnPeerStatusResult } from '@fleet-cert-console/shared';

/** A tunnel with persistent-keepalive 25 s re-handshakes at least every ~2 min; beyond 3 min the
 *  peer is not exchanging traffic with the hub. Standard WireGuard-dashboard threshold. */
export const ONLINE_THRESHOLD_MS = 3 * 60_000;

/** Whether a peer counts as online, judged against when the hub was read (not the client clock). */
export function peerOnline(peer: VpnPeerStatus | undefined, fetchedAt: string): boolean {
  if (!peer?.latestHandshakeAt) return false;
  return new Date(fetchedAt).getTime() - new Date(peer.latestHandshakeAt).getTime() < ONLINE_THRESHOLD_MS;
}

/** The "VPN online" indicator for one gateway. Null when the gateway has no WireGuard key yet: it is
 *  not enrolled, so no peer can exist on the hub and "offline" would mislead (the table shows a dash). */
export function vpnOnlineIndicator(
  gatewayPubKey: string | undefined,
  peer: VpnPeerStatus | undefined,
  fetchedAt: string,
): { type: StatusIndicatorProps.Type; label: string } | null {
  if (!gatewayPubKey) return null;
  return peerOnline(peer, fetchedAt) ? { type: 'success', label: 'online' } : { type: 'stopped', label: 'offline' };
}

/** Index a peer-status result by WireGuard public key (joins to VpnGatewayRecord.gatewayPubKey). */
export function peersByKey(result: VpnPeerStatusResult): Map<string, VpnPeerStatus> {
  return new Map(result.peers.map((p) => [p.publicKey, p]));
}

/** Compact relative time for "Last seen": "just now", "4 min ago", "3 h ago", "2 d ago". */
export function timeAgo(iso: string, nowMs: number = Date.now()): string {
  const diff = Math.max(0, nowMs - new Date(iso).getTime());
  if (diff < 60_000) return 'just now';
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  return `${Math.floor(hours / 24)} d ago`;
}

/** Human-readable byte count for transfer stats (SI units, one decimal). */
export function formatBytes(n: number): string {
  if (n < 1000) return `${n} B`;
  const units = ['kB', 'MB', 'GB', 'TB'];
  let value = n;
  let unit = '';
  for (const u of units) {
    value /= 1000;
    unit = u;
    if (value < 1000) break;
  }
  return `${value.toFixed(1)} ${unit}`;
}
